import { Injectable } from "@angular/core";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { getUser } from "@edp-store/selectors/user.selectors";
import { take, withLatestFrom } from "rxjs/operators";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { EdpUser } from "@edp-models/user.model";
import { Contract } from "@edp-components/edp-contract";
import { IAppState } from "@edp-store/state/app.state";
import { Store } from "@ngrx/store";
import { environment } from "@edp-environments/environment";
import { getOperations } from "@edp-store/selectors/operations.selector";
import { OperationHistoryTemplate } from "@edp-models/operation-history.model";

@Injectable({
  providedIn: "root"
})
export class GoogleAnalyticsService {

    constructor(
        public google: GoogleTagManagerService,
        private store: Store<IAppState>,
    ) { }

    public pageChangeEvent(): void {
        this.store.select(getUser).pipe(
            take(1),
            withLatestFrom(this.store.select(getContract).pipe(take(1)))
        ).subscribe(([user, contract]: [EdpUser, Contract]) => {
            if (user) {
                const googleAnalyticsData: GoogleAnalyticsData = new GoogleAnalyticsData(
                    user.segment,
                    location.pathname,
                    user.uid,
                    user.edpESubscribed ? 1 : 0,
                    environment.isApp ? "app" : "web"
                );

                if (contract) {
                    googleAnalyticsData.CGas              = contract.HasG ? 1 : 0;
                    googleAnalyticsData.CSolar            = 0; // Pending data
                    googleAnalyticsData.CPackLivingEDP    = contract.HasPack ? 1 : 0;
                    googleAnalyticsData.CFE               = contract.HasElectronicInvoice ? 1 : 0;
                    googleAnalyticsData.CCartaoMobilidade = contract.HasM ? 1 : 0;
                    googleAnalyticsData.CPlanoEDPSaude    = contract.HasHealth ? 1 : 0;
                    googleAnalyticsData.CContaCerta       = contract.HasContaCerta ? 1 : 0;
                    googleAnalyticsData.CDirectDebt       = contract.HasDirectDebit ? 1 : 0;
                    googleAnalyticsData.CFatSegura        = contract.HasServiceFaturaSegura ? 1 : 0;
                }

                this.google.pushTag(googleAnalyticsData);
            }
        });
    }

    public datalayerEvent(operation: string, action?: string): void {
        const opResources = {
            false: "Insucesso",
            0: "Insucesso",
            true: "Sucesso",
            1: "Sucesso",
            B2B: "Empresarial",
            B2C: "Particulares",
            ERROR: "Insucesso erro técnico",
            ERROR_FUNC: "Insucesso erro funcional"
        };

        this.store.select(getUser).pipe(
            take(1),
            withLatestFrom(this.store.select(getOperations))
        ).subscribe(([user, operations]: [EdpUser, OperationHistoryTemplate[]]) => {
            if (user && operations) {
                const op = operations.find(op => op.operation === operation);
                if (op && op.template) {
                    const datalayerdata: Datalayerdata = new Datalayerdata(
                        user.segment,
                        location.pathname,
                        user.uid,
                        user.edpESubscribed ? 1 : 0,
                        environment.isApp ? "app" : "web"
                    );

                    // ACTION
                    datalayerdata.action = op.template.action;
                    if (action && op.template.values) {
                        if (action in op.template.values) {
                            datalayerdata.action = op.template.values[action];
                        }
                    }
                    // LABEL
                    datalayerdata.label = op.template.label;
                    const _label = datalayerdata.label.replace("#", "");
                    if (_label in opResources) {
                        datalayerdata.label = opResources[_label];
                    }
                    datalayerdata.event = op.template.category;
                    datalayerdata.category = op.template.category;

                    this.google.pushTag(datalayerdata);
                }
            }
        });
    }

    public clickEvent(eventData: ClickEventInterface): void {
        this.store.select(getUser).pipe(
            take(1)
        ).subscribe((user: EdpUser) => {
            eventData.event = "gtm.click";
            this.fillBaseData(eventData, user);

            this.google.pushTag(eventData);
        });
    }

    private fillBaseData(eventData: ClickEventInterface, user: EdpUser): void {
        eventData.UserType = user.segment;
        eventData.PageName = location.pathname;
        eventData.UserId = user.uid;
        eventData.Planeta = user.edpESubscribed ? 1 : 0;
        eventData.source = environment.isApp ? "app" : "web";
    }
}

export class GoogleAnalyticsData implements ClickEventInterface {
    constructor(
        public UserType: string,
        public PageName: string,
        public UserId: string,
        public Planeta: number,
        public source: string,
        public CGas: number = 0,
        public CSolar: number = 0,
        public CPackLivingEDP: number = 0,
        public CFE: number = 0,
        public CCartaoMobilidade: number = 0,
        public CPlanoEDPSaude: number = 0,
        public CContaCerta: number = 0,
        public CDirectDebt: number = 0,
        public CFatSegura: number = 0,
        public cardtype: string = "",
        public cardcontent: string = ""
    ) { }
}

export class Datalayerdata extends GoogleAnalyticsData {
    constructor(
        public UserType: string,
        public PageName: string,
        public UserId: string,
        public Planeta: number,
        public source: string,
        public event: string = null,
        public category: string = null,
        public action: string = null,
        public label: string = null,
    ) {
        super(UserType, PageName, UserId, Planeta, source);
    }
}

export interface ClickEventInterface {
    UserType?: string;
    PageName?: string;
    UserId?: string;
    Planeta?: number;
    source?: string;
    event?: string;
    cardtype?: string;
    cardcontent?: string;
}
